import {AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import * as d3 from 'd3';
import {DonutGraphConfigurations} from '../../tools/circle-line-graph/circle-line-graph-models';
import {KlaHttpService} from '../../tools/services/kla.http.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-circle-line-chart',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './circle-line-chart.component.html',
  styleUrls: ['./circle-line-chart.component.scss', '../../../app.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class CircleLineChartComponent implements OnInit, AfterViewInit {
  @Input()
  public config: DonutGraphConfigurations;
  public legendItems: Array<any>;
  private chartView: any;
  private chartData: any[];
  private chartRadius: number;
  private chartThickness: number;

  @Input()
  titleConfig: any;

  @Input()
  titleContainer: ElementRef;

  legendCanFitInRightSide = false;

  @ViewChild('chart', {static: true})
  private chartContainer: ElementRef;

  @ViewChild('legend', {static: true})
  private legendContainer: ElementRef;

  constructor(private httpService: KlaHttpService) {
    this.legendItems = [];
  }

  onContainerKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const firstSegment = this.chartContainer.nativeElement.querySelector('.parent-donut');
      if (firstSegment) { firstSegment.focus(); }
    }
  }

  ngOnInit() {
    if (this.config.data) {
      this.createChart(this.config);
    }
  }

  ngAfterViewInit() {
    this.determineLegendPosition();
  }

  private determineLegendPosition() {
    const chartHeight = this.chartContainer.nativeElement.offsetHeight;

    const titleHeight = this.titleContainer?.nativeElement.offsetHeight;
    const safeHightBetween = 10;
    const legendHeight = this.legendContainer.nativeElement.offsetHeight;

    const rightSideTotalHeight = (titleHeight + legendHeight) - safeHightBetween;

    this.legendCanFitInRightSide = chartHeight >= rightSideTotalHeight;
  }

  @Input('width')
  set setWidth(width: number) {
    if (!this.config) { return; }
    this.config.width = width;
    this.ngOnInit();
  }

  createViewPort(width: number, height: number) {
    d3.select(this.chartContainer.nativeElement).selectAll('.donut-graph').remove();
    return d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('class', 'pie donut-graph')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', '0 0 ' + 412 + ' ' + 408)
      .append('g')
      .attr('transform', 'translate(' + (10 + (width / 2)) + ',' + (10 + (height / 2)) + ')');
  }

  createChart(config: any) {
    const width = this.config.width;
    const height = config.height;
    const thickness = config.thickness;
    const radius = Math.min(width, height) / 2;
    const pie = d3.pie<any>().value(function (d) {
      return d.value;
    }).sort(null);
    const view = this.createViewPort(width, height);
    this.createDonutFilter(view);
    this.createInnerCircleShadow(view);
    this.createOuterDonut(view, pie(config.data), radius, thickness);
    if (this.config.isVertical) {
      this.createVerticalBullets(view, config.data);
    } else {
      this.createHorizontalBullets(view, config.data);
    }
  }

  createInnerCircleShadow(view) {
    const innerCicle = view.append('svg:defs');
    const innerCircleFilter = innerCicle.append('svg:filter')
      .attr('id', 'inner-circle-shadow')
      .attr('height', '130%');
    innerCircleFilter.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 3);

    innerCircleFilter.append('feOffset')
      .attr('dx', 0)
      .attr('dy', 0)
      .attr('result', 'offsetblur');

    innerCircleFilter.append('feComponentTransfer')
      .append('feFuncA')
      .attr('type', 'linear')
      .attr('slope', 0.2);

    const innerMerge = innerCircleFilter.append('feMerge');
    innerMerge.append('feMergeNode');

    innerMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');
  }

  createDonutFilter(view) {
    const donut = view.append('svg:defs');

    const inset_shadow = donut.append('svg:filter')
      .attr('id', 'inset-shadow')
      .attr('height', '150%')
      .attr('width', '150%');

    inset_shadow.append('svg:feOffset')
      .attr('dx', 0)
      .attr('dy', 0);

    inset_shadow.append('svg:feGaussianBlur')
      .attr('stdDeviation', 4)
      .attr('result', 'offset-blur');

    inset_shadow.append('svg:feComposite')
      .attr('operator', 'over')
      .attr('in', 'shadow')
      .attr('in2', 'SourceGraphic');

    const feMerge = inset_shadow.append('feMerge');

    feMerge.append('feMergeNode')
      .attr('in', 'offsetBlur');
    feMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');
  }

  createOuterDonut(view, data, radius, thickness) {
    const that = this;
    let firstElement;

    const arc = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius);

    const focusSegment = (targetGroup) => {
      d3.select(that.chartContainer.nativeElement).selectAll('.parent-donut').attr('tabindex', '-1');
      targetGroup.attr('tabindex', '0');
      (targetGroup.node() as SVGElement).focus();
    };

    const path = view.selectAll('path')
      .data(data)
      .enter()
      .append('g')
      .attr('class', function (d, i) {
        return 'parent-donut item' + i;
      })
      .attr('tabindex', (d, i) => i === 0 ? '0' : '-1')
      .attr('aria-label', (d) => `${d.data.title}, ${d.data.value}, chart segment`)
      .on('click', function(d) {
        that.select(view, this, d, radius, thickness);
      })
      .on('keydown', function(d) {
        const event = d3.event as KeyboardEvent;
        const allNodes = d3.select(that.chartContainer.nativeElement).selectAll('.parent-donut').nodes() as Element[];
        const currentIndex = d.index;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          const nextIndex = (currentIndex + 1) % allNodes.length;
          const nextNode = allNodes[nextIndex];
          that.select(view, nextNode, that.chartData[nextIndex], that.chartRadius, that.chartThickness);
          setTimeout(() => focusSegment(d3.select(nextNode)), 0);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          const prevIndex = (currentIndex - 1 + allNodes.length) % allNodes.length;
          const prevNode = allNodes[prevIndex];
          that.select(view, prevNode, that.chartData[prevIndex], that.chartRadius, that.chartThickness);
          setTimeout(() => focusSegment(d3.select(prevNode)), 0);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          const container = that.chartContainer.nativeElement.closest('[role="group"]') as HTMLElement;
          if (container) { container.focus(); }
        }
      })
      .append('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .style('cursor', 'pointer');

    that.select(view, d3.select(this.chartContainer.nativeElement).selectAll('.item0').node(), data[0], radius, thickness);
    this.chartView = view;
    this.chartData = data;
    this.chartRadius = radius;
    this.chartThickness = thickness;
  }

  selectByIndex(index: number) {
    const element = d3.select(this.chartContainer.nativeElement).selectAll('.item' + index).node();
    this.select(this.chartView, element, this.chartData[index], this.chartRadius, this.chartThickness);
  }

  createHorizontalBullets(view, data) {
    this.legendItems = [];
    data.forEach((item, i) => {
      this.legendItems.push({ name: item.title, color: item.color, index: i });
    });
  }

  createVerticalBullets(view, data) {
    this.legendItems = [];
    data.forEach((item, i) => {
      this.legendItems.push({ name: item.title, color: item.color, index: i });
    });
  }

  select(view, element, data, radius, thickness) {
    this.selectDonut(view, element);
    this.clearInnerArea(view, radius, thickness);
    this.selectInnerDonut(view, element, data, radius, thickness);
    this.addDesctription(view, element, data, radius, thickness);
  }

  selectInnerDonut(view, element, data, radius, thickness) {
    d3.select(this.chartContainer.nativeElement).selectAll('.selected-line').remove();

    const arcline = d3.arc<any>()
      .innerRadius(radius - thickness - 10)
      .outerRadius(radius - thickness - 5)
      .cornerRadius(20)
      .startAngle(data.startAngle)
      .endAngle(data.endAngle);

    view.append('g')
      .attr('class', 'selected-line')
      .style('pointer-events', 'none')
      .append('path')
      .attr('d', arcline)
      .attr('fill', d3.select(element).select('path').attr('fill'));
  }

  selectDonut(view, element) {
    const path = d3.select(element).select('path');
    d3.select(this.chartContainer.nativeElement).selectAll('.selected-donut').remove();
    view.append('g')
      .attr('class', 'selected-donut')
      .style('pointer-events', 'none')
      .append('path')
      .attr('d', path.attr('d'))
      .attr('fill', path.attr('fill'))
      .attr('filter', 'url(#inset-shadow)');
  }

  clearInnerArea(view, radius, thickness) {
    d3.select(this.chartContainer.nativeElement).selectAll('.selected-circle').remove();

    view.append('circle')
      .attr('class', 'selected-circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', radius - thickness)
      .attr('fill', '#ffffff')
      .style('pointer-events', 'none');
  }


  addDesctription(view, element, data, radius, thickness) {
    d3.select(this.chartContainer.nativeElement).selectAll('.wrap-description').remove();
    let titleText = data.data.title, subTitleText = data.data.value != null ? String(data.data.value) : (data.data.subTitle || '');

    if (data.data.title && data.data.title.length > 25) {
      titleText = data.data.title.substring(0, 25) + '...';
    }
    if (data.data.subTitle && data.data.subTitle.length > 25) {
      subTitleText = data.data.subTitle.substring(0, 25) + '...';
    }
    const description = view.append('g').attr('class', 'wrap-description').style('pointer-events', 'none');

    const title = description.append('text')
      .attr('class', 'heading bold')
      .text(titleText)
      .attr('text-anchor', 'middle');

    title.append('title')
      .text(data.data.title);

    if (data.data.icon) {
      title.attr('dy', '-25px');

      description.append('circle')
        .attr('cx', 0)
        .attr('cy', -80)
        .attr('r', 26)
        .attr('fill', '#ffffff')
        .attr('filter', 'url(' + window.location.href + '#inner-circle-shadow)');

      description.append('image')
        .attr('id', 'bg')
        .attr('xlink:href', this.httpService.getImagePath(data.data.icon, 40))
        .attr('x', -20)
        .attr('y', -100)
        .attr('width', 40)
        .attr('height', 40);
    } else {
      title.attr('dy', '-55px');
      description.append('text')
        .attr('class', 'heading bold')
        .text(subTitleText)
        .attr('text-anchor', 'middle')
        .attr('dy', '-25px')
        .append('title')
        .text(data.data.subTitle);
    }

    description.append('text')
      .attr('class', 'description')
      .text(data.data.description)
      .attr('text-anchor', 'middle')
      .attr('dy', '35px');
    this.multilineText(d3.select(this.chartContainer.nativeElement).selectAll('.description').node(), radius, 13);

    description.append('rect')
      .attr('x', -10)
      .attr('width', 20)
      .attr('height', 6)
      .attr('rx', 4)
      .style('fill', d3.select(element).select('path').attr('fill'));
  }

  multilineText(element, width, lineHeight) {
    let text = d3.select(element),
      words = text.text().split(/\s+/).reverse(),
      word,
      line = [],
      tspan = text.text(null).append('tspan');

    while (word = words.pop()) {
      line.push(word);
      tspan.text(line.join(' '));
      if (tspan.node()['getComputedTextLength']() > width) {
        line.pop();
        tspan.text(line.join(' '));
        line = [word];
        tspan = text.append('tspan').attr('x', 0).attr('dy', lineHeight + 'px').text(word);
      }
    }
  }

}
